import {useCallback, useEffect, useMemo, useState} from "react";
import {
  useResourcePersonCourses,
  useResourcePersonLessons,
  useResourcePersonSubmissions,
  useResourcePersonEarnings,
} from "../store/resourcePerson/resourcePersonStore";

/**
 * Shared data layer for the "assessment" area pages (Quizzes, Assignments,
 * Reports, Students).
 *
 * These four pages used to render hard-coded arrays — invented quiz scores,
 * attempt counts and student names that no API ever produced. They now read the
 * resource person's real content instead. There is no standalone /quizzes or
 * /assignments endpoint in the API, so each page is derived from what the
 * author actually owns:
 *
 *   Quizzes     ← QUIZ lessons and their questions, across the author's courses
 *   Assignments ← lesson submissions across courses
 *   Reports     ← per-course analytics + the earnings summary
 *   Students    ← enrolled students per course
 *
 * Every figure below therefore comes from a confirmed route. Anything the API
 * does not report (a per-quiz pass rate, for example) is left null and rendered
 * as "—" rather than invented, so nothing on screen can be mistaken for real
 * data.
 *
 * Callers get a course picker: several of these endpoints are course-scoped, so
 * `selectedCourseId` chooses which course the page is describing.
 */
export function useAssessmentData() {
  const {
    courses,
    coursesLoading,
    coursesError,
    courseStudents,
    courseStudentsLoading,
    courseStudentsError,
    courseStudentsTotal,
    courseAnalytics,
    courseAnalyticsLoading,
    fetchCourses,
    fetchCourseStudents,
    fetchCourseAnalytics,
  } = useResourcePersonCourses();

  const {
    lessons,
    lessonsLoading,
    lessonsError,
    fetchLessons,
    fetchAssessments,
    fetchQuestions,
    questions,
    questionsLoading,
  } = useResourcePersonLessons();

  const {
    submissions,
    submissionsLoading,
    submissionsError,
    fetchSubmissions,
  } = useResourcePersonSubmissions();

  const {
    earningsSummary,
    earningsAnalytics,
    fetchEarningsSummary,
    fetchEarningsAnalytics,
  } = useResourcePersonEarnings();

  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [lessonsCourseId, setLessonsCourseId] = useState("");

  // Course list is the spine of this whole area — load it once.
  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]);

  // Default the picker to the first course once the list arrives.
  useEffect(() => {
    if (!selectedCourseId && courses.length) setSelectedCourseId(courses[0].id);
  }, [courses, selectedCourseId]);

  // Course-scoped loads. Each has a live counterpart, so a failure on one must
  // not blank the others — they simply report their own error.
  useEffect(() => {
    if (!selectedCourseId) return;
    let cancelled = false;

    const loadCourse = async () => {
      setLocalLoading(true);
      setLocalError(null);
      setLessonsCourseId("");
      const [lessonResult, studentResult, analyticsResult, submissionResult] = await Promise.all([
        fetchLessons(selectedCourseId),
        fetchCourseStudents(selectedCourseId),
        fetchCourseAnalytics(selectedCourseId),
        fetchSubmissions(selectedCourseId),
      ]);
      if (cancelled) return;
      setLocalLoading(false);
      if (lessonResult.success) setLessonsCourseId(String(selectedCourseId));
      const failed = [lessonResult, studentResult, analyticsResult, submissionResult].find(
        (r) => !r.success
      );
      if (failed) setLocalError(failed.error || "Could not load this course's data.");
    };
    loadCourse();

    return () => {
      cancelled = true;
    };
  }, [selectedCourseId, fetchLessons, fetchCourseStudents, fetchCourseAnalytics, fetchSubmissions]);

  // Earnings are account-wide, not course-scoped.
  useEffect(() => {
    fetchEarningsSummary();
    fetchEarningsAnalytics();
  }, [fetchEarningsSummary, fetchEarningsAnalytics]);

  /** Load assessments and questions through the Postman-nested lesson routes. */
  const loadAssessments = useCallback(
    (courseId, lessonId) => fetchAssessments(courseId, lessonId),
    [fetchAssessments]
  );
  const loadQuestions = useCallback(
    (courseId, lessonId, assessmentId) => fetchQuestions(courseId, lessonId, assessmentId),
    [fetchQuestions]
  );

  const selectedCourse = useMemo(
    () => courses.find((c) => String(c.id) === String(selectedCourseId)) || null,
    [courses, selectedCourseId]
  );

  // Consumers use this array as an effect dependency (Assessment Center).
  // Without memoising it, a new array on every render makes that effect run
  // forever: setAssessments → re-render → new lessons array → effect again.
  const lessonsList = useMemo(() => (lessons || []).slice(), [lessons]);

  return {
    // picker
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    selectedCourse,

    // course-scoped
    lessons: lessonsList,
    lessonsCourseId,
    lessonsLoading: lessonsLoading || localLoading,
    lessonsError: lessonsError || localError,
    courseStudents: courseStudents || [],
    courseStudentsLoading,
    courseStudentsError,
    courseStudentsTotal,
    courseAnalytics,
    courseAnalyticsLoading,
    submissions: submissions || [],
    submissionsLoading,
    submissionsError,

    // account-wide
    earningsSummary,
    earningsAnalytics,
    questions,
    questionsLoading,
    loadAssessments,
    loadQuestions,
  };
}

/** Coerce anything to a finite number; used instead of inventing averages. */
export const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default useAssessmentData;